"use client";
import { useState, useRef, useMemo } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
// import "swiper/css";

import Img1 from "@/assets/life/classrooms.png";
import Img2 from "@/assets/life/computer-lab.png";
import Img3 from "@/assets/life/food-lab.png";
import Img4 from "@/assets/life/health-lab.png";
import Img5 from "@/assets/life/img-5.png";
import Title from "../Title";
import { ArrowRight } from "lucide-react";
import VideoModal from "@/components/videoModel";
import Link from "next/link";
import virtualTour from "@/assets/life/virtual-tour.webp";

// Media URL helper for Strapi
const getMediaUrl = (media) => {
  const baseUrl = process.env.NEXT_PUBLIC_STRAPI_URL || "http://localhost:1337";
  const url = media?.url || media?.data?.attributes?.url || media?.data?.url;
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${baseUrl}${url}`;
};

// Rich Text helper for Strapi Blocks
const renderRichText = (blocks) => {
  if (typeof blocks === "string") return blocks;
  if (Array.isArray(blocks)) {
    return blocks
      .map((block) => {
        if (block.type === "paragraph") {
          return block.children.map((child) => child.text).join("");
        }
        return "";
      })
      .join("\n");
  }
  return "";
};

const defaultCampusData = [
  {
    title: "Classrooms",
    desc: "Modern classrooms with smart technology, designed to enhance interactive learning and collaboration.",
    img: Img1,
    url: "infrastructure",
  },
  {
    title: "Allied Health Labs",
    desc: "State-of-the-art health labs offering practical exposure to diagnostics, patient care, and research using industry-standard tools.",
    img: Img4,
    url: "infrastructure",
  },
  {
    title: "Food Production Labs",
    desc: "Professional culinary labs equipped with modern tools for real-world hands-on training in hospitality and food technology.",
    img: Img3,
    url: "infrastructure",
  },
  {
    title: "Computer Labs",
    desc: "Advanced computer labs providing the best hardware and software resources to explore modern computing and innovation.",
    img: Img2,
    url: "infrastructure",
  },
  {
    title: "Experience a Virtual Campus Tour",
    desc: "",
    img: virtualTour,
    video: "https://cdn.msu.edu.in/campus/Msu-Campus-Walkthrough.mp4",
  },
];

const CampusSection = ({ data, loading }) => {
  const [modal, setModal] = useState({ open: false, url: "" });

  const hoverVideoRef = useRef(null);

  const handleVideoHover = () => {
    if (hoverVideoRef.current) {
      hoverVideoRef.current.currentTime = 0;
      hoverVideoRef.current.play().catch(() => {});
    }
  };

  const handleVideoLeave = () => {
    if (hoverVideoRef.current) {
      hoverVideoRef.current.pause();
      hoverVideoRef.current.currentTime = 0;
    }
  };

  // Map Strapi Infrastructure_block data to display format
  const dynamicCampusData = useMemo(() => {
    const blocks = data?.Infrastructure_block;
    if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return [];
    return blocks.map((block) => {
      const imgMedia = block.Image;
      const imgUrl = getMediaUrl(imgMedia);
      return {
        title: block.Title || "",
        desc: renderRichText(block.Description) || "",
        img: imgUrl || "",
        url: block.URL || "",
        video: block.videoUrl || "",
        blockType: block.BlockType || "",
        isDynamic: true,
      };
    });
  }, [data]);

  const campusData =
    dynamicCampusData.length > 0 ? dynamicCampusData : defaultCampusData;
  const isDynamic = dynamicCampusData.length > 0;

  // Section title and description
  const sectionTitle = data?.Title || "Where Learning Meets Industry Reality";
  const sectionDescription =
    renderRichText(data?.Description) ||
    "With 16+ specialised labs spanning allied health, food production, computing, and more, our world-class campus infrastructure equips students with hands-on experience for industry success.";

  // Separate regular cards and video card
  const regularCards = campusData.filter((card) => !card.video);
  const videoCard = campusData.find((card) => card.video);

  const [expanded, setExpanded] = useState(null);

  // Helper to render image (dynamic uses <img>, static uses Next.js <Image>)
  const renderImage = (card, width, height, className) => {
    if (isDynamic) {
      return <img src={card.img} alt={card.title} className={className} loading="lazy"/>;
    }
    return (
      <Image
        src={card.img}
        alt={card.title}
        width={width}
        height={height}
        className={className}
        loading="lazy"
      />
    );
  };

  return (
    <div className="relative w-full top_bottom_space">
      <section className="max-w-[var(--max-page-width)] mx-auto px-[var(--container-padding)]">
        <VideoModal
          open={modal.open}
          url={modal.url}
          onClose={() => setModal({ open: false, url: "" })}
        />
        <div className="w-full heading_space_bottom">
          <Title
            heading={sectionTitle}
            subText={sectionDescription}
            color="text-black"
            subTextColor="text-black/70"
            align="text-left"
          />
        </div>
        <div className="w-full">
          <style jsx>{`
            .bg-gradient-expanded {
              background: linear-gradient(
                180deg,
                rgba(8, 30, 45, 0) 45.54%,
                #081e2d 91.22%
              );
            }
          `}</style>

          <div className="hidden md:grid grid-cols-1 lg:grid-cols-[2.2fr_2fr] gap-3 w-full">
            <div className="h-[650px] grid grid-cols-2 gap-3 auto-rows-auto lg:order-0 md:order-2">
              {regularCards.slice(0, 4).map((card, index) => (
                <div key={index} className="relative group overflow-hidden">
                  {renderImage(
                    card,
                    800,
                    600,
                    "w-full h-auto object-cover transform transition-transform duration-500 ease-in-out group-hover:scale-105",
                  )}

                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(8, 30, 45, 0) -17.07%, rgba(8, 30, 45, 0.58) 19.06%, #081E2D 82.45%)",
                    }}
                  />

                  {/* bottom content */}
                  <div className="absolute bottom-0 left-0 right-0 z-10">
                    <div
                      className="w-full p-4 pr-16 transition-all duration-500 ease-in-out"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(8,30,45,0) 45.54%, #081e2d 91.22%)",
                      }}
                    >
                      <p className="text-white font-semibold text-[20px]">
                        {card.url && index === 0 ? (
                          <Link href={`/${card.url}`}>
                            {card.title}
                          </Link>
                        ) : <span className="cursor-pointer" onClick={() => card.url && (window.location.href = card.url)}>
                        {card.title}</span>}
                      </p>

                      <p
                        className={`text-gray-200 text-[14px] mt-2 transform translate-y-2 hidden group-hover:block 
                      group-hover:translate-y-0 transition-all duration-300`}
                      >
                        {card.desc}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`
                    absolute cursor-pointer a material-symbols-outlined text-lg
                    transition-all duration-300 bottom-4 right-4 text-white z-10

                    group-hover:bg-white group-hover:text-black
                    group-hover:rounded-full group-hover:p-2
                  `}
                    onClick={() =>
                      card.url && (window.location.href = card.url)
                    }
                    aria-label="Navigate to RIght"
                  >
                    <ArrowRight />
                  </button>
                </div>
              ))}
            </div>

            {videoCard && (
              <div className="h-[650px] relative overflow-hidden order-1">
                {renderImage(
                  videoCard,
                  1200,
                  800,
                  "object-cover w-full h-full",
                )}
                <div className="absolute inset-0 bg-black/20 pointer-events-none" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                  <button
                    onClick={() =>
                      setModal({
                        open: true,
                        url: videoCard.video,
                      })
                    }
                    className="pointer-events-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white bg-opacity-80 flex items-center justify-center"
                  >
                    <Image
                      src="/play.svg"
                      alt="Play"
                      width={18}
                      height={22}
                      className="ml-1.5 ccursor-pointer"
                    />
                  </button>
                </div>

                <div className="absolute top-0 inset-0 flex flex-col justify-end via-transparent to-transparent z-10">
                  <div
                    className={`w-full p-4 transition-all duration-500 ease-in-out
              ${
                expanded === "video"
                  ? "bg-gradient-expanded border-0"
                  : "bg-[#0B202C]"
              }
            `}
                  >
                    <p className="text-white text-[20px] font-semibold">
                      {videoCard.title}
                    </p>

                    {expanded === "video" && (
                      <p className="text-gray-200 text-[14px] mt-2">
                        {videoCard.desc}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="md:hidden">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={16}
              loop={true}
              slidesPerView={1.2}
              autoplay={{ delay: 2500 }}
            >
              {campusData.map((card, index) => (
                <SwiperSlide key={index}>
                  <div className="relative overflow-hidden">
                    {renderImage(card, 800, 600, "object-cover w-full h-auto")}

                    <div
                      className="absolute inset-0 opacity-100 transition-opacity duration-300 z-2"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(8, 30, 45, 0) -17.07%, rgba(8, 30, 45, 0.58) 19.06%, #081E2D 82.45%)",
                      }}
                    ></div>

                    {card.video && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-15">
                        <button
                          onClick={() =>
                            setModal({
                              open: true,
                              url: card.video,
                            })
                          }
                          className="pointer-events-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white bg-opacity-80 flex items-center justify-center"
                        >
                          <Image
                            src="/play.svg"
                            alt="Play"
                            width={18}
                            height={22}
                            className="ml-1.5 ccursor-pointer"
                          />
                        </button>
                      </div>
                    )}

                    <div className="absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/70 via-transparent to-transparent z-10">
                      <div className="w-full p-4 bg-gradient-expanded transition-all duration-500 ease-in-out pr-14">
                        <p className="text-white text-[18px] font-semibold">
                          {card.url ? (
                            <Link href={`/${card.url}`}>{card.title}</Link>
                          ) : (
                            card.title
                          )}
                        </p>

                        <p className="text-gray-200 text-[14px] mt-2 z-21">
                          {card.desc}
                        </p>
                      </div>

                      {!card.video && (
                        <span
                          onClick={() =>
                            card.url && (window.location.href = card.url)
                          }
                        >
                          <button
                            aria-label="Go to Infrastructure page"
                            className={`absolute bottom-4 right-4 cursor-pointer material-symbols-outlined text-lg  test
                          transition-all duration-300 z-50 p-2 bg-white text-black rounded-full`}
                          >
                            <ArrowRight />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CampusSection;
